import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Clock, Award, ArrowLeft, ArrowRight, X, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import BadgeCelebration from './BadgeCelebration';

const BADGE_HERO = "https://static.prod-images.emergentagent.com/jobs/22029687-1289-4783-879e-410ec2896cc4/images/cd950c6813bcde0516be129f7c2fe4ae9766c55cd86a76f55b4c3f07c7f8e656.png";

export default function QuizTaker({ child, isSubscribed = false, onSubscribeClick }) {
  const [quizzes, setQuizzes] = useState([]);
  const [completedMap, setCompletedMap] = useState({}); // { quiz_id: { percentage, score, total_points, completed_at } }
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [celebrateBadges, setCelebrateBadges] = useState([]);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (!child?.grade || !child?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [quizRes, resultRes] = await Promise.all([
          api.get(`/api/quizzes?grade=${encodeURIComponent(child.grade)}`),
          api.get(`/api/quiz-results?child_id=${encodeURIComponent(child.id)}`),
        ]);
        setQuizzes(quizRes.data);
        const map = {};
        (resultRes.data || []).forEach((r) => {
          // Keep the best (highest %) if somehow multiple exist (legacy data)
          if (!map[r.quiz_id] || r.percentage > map[r.quiz_id].percentage) {
            map[r.quiz_id] = {
              percentage: r.percentage,
              score: r.score,
              total_points: r.total_points,
              completed_at: r.completed_at,
            };
          }
        });
        setCompletedMap(map);
      } catch { toast.error('Failed to load quizzes'); }
      finally { setLoading(false); }
    })();
  }, [child?.grade, child?.id]);

  // Timer effect
  useEffect(() => {
    if (!activeQuiz || result || submittedRef.current) return;
    if (secondsLeft <= 0) return;
    const t = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(t);
          // Auto-submit on timeout
          if (!submittedRef.current) {
            submittedRef.current = true;
            submitQuiz(true);
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuiz, result, secondsLeft]);

  const startQuiz = (quiz) => {
    if (completedMap[quiz.id]) {
      toast.info('This quiz has already been completed 🎉');
      return;
    }
    if (!isSubscribed) {
      toast.info('Subscribe to start quizzes');
      onSubscribeClick?.();
      return;
    }
    setActiveQuiz(quiz);
    setAnswers(new Array(quiz.questions.length).fill(-1));
    setQIndex(0);
    setResult(null);
    submittedRef.current = false;
    setSecondsLeft((quiz.time_limit_minutes || 10) * 60);
  };

  const selectOption = (i) => {
    const next = [...answers]; next[qIndex] = i; setAnswers(next);
  };

  const submitQuiz = async (isAutoSubmit = false) => {
    if (!isAutoSubmit && answers.some((a) => a === -1)) {
      toast.error('Please answer all questions'); return;
    }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await api.post('/api/quizzes/submit', {
        quiz_id: activeQuiz.id, child_id: child.id,
        // Replace any unanswered (-1) with 0 on auto-submit
        answers: answers.map((a) => (a === -1 ? 0 : a)),
      });
      setResult(data);
      // Cache into completedMap so the card locks immediately
      setCompletedMap((prev) => ({
        ...prev,
        [activeQuiz.id]: {
          percentage: data.percentage,
          score: data.score,
          total_points: data.total_points,
          completed_at: data.completed_at,
        },
      }));
      if (isAutoSubmit) toast.warning("Time's up — submitted!");
      else toast.success(`Score: ${Math.round(data.percentage)}%`);
      if (data.new_badges?.length > 0) {
        // Delay celebration a bit so result screen renders first
        setTimeout(() => setCelebrateBadges(data.new_badges), 600);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
      submittedRef.current = false;
    } finally { setSubmitting(false); }
  };

  const closeQuiz = () => {
    setActiveQuiz(null); setResult(null); setAnswers([]); setQIndex(0);
    setSecondsLeft(0);
    submittedRef.current = false;
  };

  const formatTime = (s) => {
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-[#38BDF8] border-t-transparent rounded-full" />
      </div>
    );
  }

  // RESULT
  if (activeQuiz && result) {
    const pct = Math.round(result.percentage);
    const color = pct >= 80 ? 'text-[#16A34A]' : pct >= 60 ? 'text-[#CA8A04]' : 'text-[#FD5E53]';
    const message = pct >= 80 ? 'Amazing work! 🎉' : pct >= 60 ? 'Great effort! Keep going 💪' : 'Good try! Practice makes perfect ✨';
    return (
      <>
        <div className="bg-white rounded-[2rem] p-8 sm:p-12 text-center max-w-2xl mx-auto shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50" data-testid="quiz-result">
        <motion.img
          src={BADGE_HERO}
          alt=""
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="w-32 h-32 sm:w-44 sm:h-44 mx-auto mb-4 object-contain drop-shadow-xl"
        />
        <p className="font-nunito text-xs font-bold uppercase tracking-wider text-[#FD5E53] mb-2">Quiz Complete</p>
        <h2 className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 mb-1">{activeQuiz.title}</h2>
        <p className={`font-fredoka text-6xl sm:text-7xl font-semibold my-4 ${color}`}>{pct}%</p>
        <p className="font-nunito text-slate-500 mb-2">
          {result.score} / {result.total_points} points
        </p>
        <p className="font-fredoka text-slate-700 text-lg mb-6">{message}</p>
        <button
          onClick={closeQuiz}
          className="bg-[#FD5E53] hover:bg-[#E84E44] text-white px-7 py-3 rounded-full font-fredoka font-medium transition-all hover:-translate-y-0.5 shadow-md"
          data-testid="quiz-done-btn"
        >
          Back to Quizzes
        </button>
      </div>
      {celebrateBadges.length > 0 && (
        <BadgeCelebration badges={celebrateBadges} onClose={() => setCelebrateBadges([])} />
      )}
      </>
    );
  }

  // ACTIVE QUIZ
  if (activeQuiz) {
    const q = activeQuiz.questions[qIndex];
    const timeLow = secondsLeft <= 30;
    return (
      <div className="bg-white rounded-[2rem] p-6 sm:p-10 max-w-3xl mx-auto shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50" data-testid="quiz-taker">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="font-nunito text-xs font-bold uppercase tracking-wider text-[#FD5E53] mb-1">
              Question {qIndex + 1} of {activeQuiz.questions.length}
            </p>
            <h2 className="font-fredoka text-xl sm:text-2xl font-semibold text-slate-800 truncate">{activeQuiz.title}</h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Countdown timer */}
            <motion.div
              animate={timeLow ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.8, repeat: timeLow ? Infinity : 0 }}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-fredoka font-semibold ${
                timeLow
                  ? 'bg-[#FFE4E6] text-[#FD5E53]'
                  : 'bg-slate-100 text-slate-700'
              }`}
              data-testid="quiz-timer"
            >
              {timeLow ? <AlertCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              {formatTime(secondsLeft)}
            </motion.div>
            <button onClick={closeQuiz} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-testid="quiz-close-btn">
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="h-2 bg-slate-100 rounded-full mb-7 overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-[#38BDF8] to-[#FD5E53]"
            initial={{ width: 0 }}
            animate={{ width: `${((qIndex + 1) / activeQuiz.questions.length) * 100}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={qIndex} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h3 className="font-fredoka text-lg sm:text-2xl font-semibold text-slate-800 mb-6 leading-snug">{q.question}</h3>
            <div className="space-y-3">
              {q.options.map((opt, i) => {
                const selected = answers[qIndex] === i;
                return (
                  <button
                    key={i}
                    onClick={() => selectOption(i)}
                    className={`w-full text-left p-4 sm:p-5 rounded-2xl border-2 transition-all font-nunito flex items-center gap-4 ${
                      selected
                        ? 'border-[#38BDF8] bg-[#E0F2FE] text-slate-800'
                        : 'border-slate-100 hover:border-slate-200 bg-white'
                    }`}
                    data-testid={`quiz-option-${i}`}
                  >
                    <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-fredoka font-semibold text-sm flex-shrink-0 transition-colors ${
                      selected ? 'bg-[#38BDF8] text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="font-medium">{opt}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between mt-8">
          <button
            onClick={() => setQIndex(Math.max(0, qIndex - 1))}
            disabled={qIndex === 0}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-slate-100 font-fredoka font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            data-testid="quiz-prev-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </button>
          {qIndex < activeQuiz.questions.length - 1 ? (
            <button
              onClick={() => setQIndex(qIndex + 1)}
              disabled={answers[qIndex] === -1}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#38BDF8] hover:bg-[#0EA5E9] text-white font-fredoka font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
              data-testid="quiz-next-btn"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => submitQuiz(false)}
              disabled={submitting || answers[qIndex] === -1}
              className="px-7 py-2.5 rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white font-fredoka font-medium disabled:opacity-40 transition-all hover:-translate-y-0.5"
              data-testid="quiz-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit quiz'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // LIST
  return (
    <div data-testid="quiz-list">
      <div className="mb-6">
        <h2 className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
          Quizzes for {child.name}
        </h2>
        <p className="font-nunito text-slate-500">Grade {child.grade} · {quizzes.length} available</p>
      </div>

      {!isSubscribed && quizzes.length > 0 && (
        <div
          className="mb-5 rounded-3xl border border-[#FDE68A] bg-gradient-to-r from-[#FEF9C3] to-[#FED7AA] p-5 flex flex-wrap items-center gap-4 justify-between"
          data-testid="quiz-locked-banner"
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-2xl bg-white/70 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-[#D97706]" />
            </div>
            <div className="min-w-0">
              <p className="font-fredoka text-slate-800 text-base font-semibold leading-snug">
                Subscribe to unlock quizzes
              </p>
              <p className="font-nunito text-sm text-slate-600">
                You can see all {quizzes.length} quiz{quizzes.length !== 1 ? 'zes' : ''} — subscribe to start earning points & badges.
              </p>
            </div>
          </div>
          {onSubscribeClick && (
            <button
              onClick={onSubscribeClick}
              className="bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-sm px-5 py-2.5 rounded-full shadow-sm transition-all hover:-translate-y-0.5"
              data-testid="quiz-unlock-subscribe-btn"
            >
              Subscribe now
            </button>
          )}
        </div>
      )}

      {quizzes.length === 0 ? (
        <div className="bg-white rounded-[2rem] p-10 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50">
          <ClipboardList className="w-12 h-12 text-slate-200 mx-auto mb-3" />
          <p className="font-nunito text-slate-500">No quizzes available yet.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {quizzes.map((quiz, i) => {
            const done = completedMap[quiz.id];
            const pct = done ? Math.round(done.percentage) : null;
            const scoreColor = pct === null ? '' : pct >= 80 ? 'text-[#16A34A]' : pct >= 60 ? 'text-[#CA8A04]' : 'text-[#FD5E53]';
            const subLocked = !done && !isSubscribed;
            const disabled = !!done || subLocked;
            return (
              <motion.div
                key={quiz.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={disabled ? {} : { y: -4 }}
                className={`relative bg-white rounded-[1.75rem] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border transition-all ${
                  done
                    ? 'border-[#16A34A]/25 bg-gradient-to-br from-white to-[#F0FDF4]'
                    : subLocked
                      ? 'border-slate-100/50 opacity-80'
                      : 'border-slate-100/50 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]'
                }`}
                data-testid={`quiz-card-${quiz.id}`}
              >
                {done && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#16A34A] text-white font-nunito text-[11px] font-bold uppercase tracking-wide shadow-sm"
                    data-testid={`quiz-completed-badge-${quiz.id}`}
                  >
                    <CheckCircle2 className="w-3 h-3" /> Completed
                  </span>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center relative overflow-hidden ${done ? 'bg-[#DCFCE7]' : 'bg-[#FEF9C3]'}`}>
                    <Award className={`w-6 h-6 ${done ? 'text-[#16A34A]' : 'text-[#CA8A04]'}`} />
                    {subLocked && (
                      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] flex items-center justify-center rounded-2xl">
                        <Lock className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  {!done && (
                    <span className="text-xs font-nunito font-semibold text-slate-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> {quiz.time_limit_minutes}m
                    </span>
                  )}
                </div>
                <h3 className="font-fredoka text-lg font-medium text-slate-800 mb-1 pr-20">{quiz.title}</h3>
                <p className="font-nunito text-sm text-slate-500 mb-1">{quiz.subject_name || 'General'}</p>
                {done ? (
                  <div className="mb-4 mt-2">
                    <p className={`font-fredoka text-3xl font-semibold ${scoreColor}`}>{pct}%</p>
                    <p className="font-nunito text-xs text-slate-500">
                      {done.score} / {done.total_points} points · finished
                    </p>
                  </div>
                ) : (
                  <p className="font-nunito text-xs text-slate-400 mb-5">
                    {quiz.questions?.length || 0} questions · {quiz.total_points} pts
                  </p>
                )}
                <button
                  onClick={() => startQuiz(quiz)}
                  disabled={!!done}
                  className={`w-full py-2.5 rounded-full font-fredoka font-medium transition-all shadow-sm flex items-center justify-center gap-1.5 ${
                    done
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : subLocked
                        ? 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        : 'bg-[#FD5E53] hover:bg-[#E84E44] text-white hover:-translate-y-0.5'
                  }`}
                  data-testid={done ? `quiz-locked-${quiz.id}` : subLocked ? `quiz-sub-locked-${quiz.id}` : `start-quiz-${quiz.id}`}
                >
                  {done ? (
                    <><Lock className="w-4 h-4" /> Already completed</>
                  ) : subLocked ? (
                    <><Lock className="w-4 h-4" /> Subscribe to start</>
                  ) : (
                    'Start quiz'
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
